import { HocuspocusProvider } from "@hocuspocus/provider";
import { Editor, Extension, JSONContent } from "@tiptap/core";
import { Editor as CoreEditor } from "@tiptap/core";
import BulletList from "@tiptap/extension-bullet-list";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Highlight from "@tiptap/extension-highlight";
import ListKeymap from "@tiptap/extension-list-keymap";
import OrderedList from "@tiptap/extension-ordered-list";
import Placeholder from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import Typography from "@tiptap/extension-typography";
import Underline from "@tiptap/extension-underline";
import { ReactNodeViewRenderer } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { SuggestionPluginKey } from "@tiptap/suggestion";
import json5 from "json5";
import { isArray, isEmpty } from "lodash";
import isString from "lodash/isString";
import { lowlight } from "lowlight/lib/common";
import { GapCursor } from "prosemirror-gapcursor";
import { Plugin, PluginKey, TextSelection } from "prosemirror-state";
import { PaidFeaturesNode } from "types/graphql-schema";
import { IndexeddbPersistence } from "y-indexeddb";
import * as Y from "yjs";

import DeprecatedKpiExtension from "@components/wysiwyg/extensions/artifacts/kpi-embed/deprecated-kpi-artifact";
import DeprecatedGithubExtension from "@components/wysiwyg/extensions/deprecated-github";
import UrlPreview from "@components/wysiwyg/extensions/url-preview";

import ActionItem from "./extensions/artifacts/action-item-extension";
import AsanaExtension from "./extensions/artifacts/asana/asana-extension";
import Decision from "./extensions/artifacts/decision-extension";
import Document from "./extensions/artifacts/document-extension";
import Feedback from "./extensions/artifacts/feedback-extension";
import Goal from "./extensions/artifacts/goal-extension";
import KpiEmbedExtension from "./extensions/artifacts/kpi-embed/kpi-embed";
import KpiGroupEmbedExtension from "./extensions/artifacts/kpi-embed/kpi-group-embed";
import DeprecatedRatingExtension from "./extensions/artifacts/rating/rating-artifact";
import Recognition from "./extensions/artifacts/recognition-extension";
import ButtonExtension from "./extensions/button";
import CodeBlockComponent from "./extensions/code-bock";
import Commands from "./extensions/commands";
import { getSuggestionItems } from "./extensions/commands-items";
import { renderItems } from "./extensions/commands-render";
import { Comment } from "./extensions/comment/comment-extension";
import Iframe from "./extensions/embeds/iframe";
import ExplorerExtension from "./extensions/explorer-extension";
import CustomImage from "./extensions/image";
import { asyncUploadImage } from "./extensions/image-helpers";
import CustomLink from "./extensions/link";
import CustomListItem from "./extensions/list-item";
import MeetingEmbedExtension from "./extensions/meeting-embed";
import { CustomMention } from "./extensions/mention";
import Timer from "./extensions/timer";

export const toggleLink = (editor: CoreEditor) => {
  let selectedText = "";
  const { from, to, empty } = editor.state.selection;
  if (!empty) {
    selectedText = editor.state.doc.textBetween(from, to, " ");
  }
  if (editor.isActive("link")) {
    return editor.chain().focus().unsetLink().run();
  } else if (selectedText) {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);
    // cancelled
    if (url === null) {
      return false;
    }
    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return false;
    }
    // update link
    return editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();
  }
  return false;
};

export const getTipTapCreatedByUser = (user: any) => {
  if (!user || !user.id || !user.name) return null;

  return {
    __typename: "UserNode",
    id: user.id,
    name: user.name,
  };
};

export const isEmptyValue = (value: any) => {
  const isEmptyTiptapJSON = (jsonStr: any) => {
    if (
      jsonStr.type === "doc" &&
      jsonStr.content.every((c: any) => {
        return (
          c.type === "paragraph" &&
          (!c.content ||
            (!isArray(c.content) && c.content.trim() === "") ||
            (isArray(c.content) &&
              c.content.every(
                (t: any) => t.type === "text" && t.text.trim() === "",
              )))
        );
      })
    ) {
      return true;
    }
    return false;
  };
  if (!value) {
    return true;
  }
  if (isEmpty(value)) {
    return true;
  }
  if (isString(value)) {
    if (value === "" || value === "{}") {
      return true;
    }
    try {
      return isEmptyTiptapJSON(json5.parse(value));
    } catch {
      return false;
    }
  }
  return isEmptyTiptapJSON(value);
};

export const convertToTiptapParagraph = (str: string) => {
  return {
    type: "doc",
    content: [{ type: "paragraph", content: [{ type: "text", text: str }] }],
  };
};

export const cursorColors = [
  "#f783ac",
  "#f87171",
  "#f97316",
  "#f59e0b",
  "#84cc16",
  "#10b981",
  "#06b6d4",
  "#60a5fa",
  "#a78bfa",
  "#e879f9",
  "#fb7185",
];

const providerRegistry = new Map<
  string,
  {
    ydoc: Y.Doc | undefined;
    providerWebsocket: HocuspocusProvider | null;
    references: number;
  }
>();

export const destroyYDocAndWebsocketProvider = (socketId: string) => {
  const existing = providerRegistry.get(socketId);
  if (existing) {
    existing.references -= 1;
    if (existing.references > 0) return;
    existing.providerWebsocket?.destroy();
    existing.ydoc?.destroy();
    providerRegistry.delete(socketId);
  }
};

export const getYDocAndWebsocketProvider = (
  socketId: string,
  websocketToken: string,
) => {
  const existing = providerRegistry.get(socketId);
  if (existing) {
    existing.references += 1;
    return existing;
  }

  const ydoc = socketId ? new Y.Doc() : undefined;
  if (ydoc) {
    new IndexeddbPersistence(socketId, ydoc);
  }

  const providerWebsocket =
    !socketId || window.Cypress
      ? null
      : new HocuspocusProvider({
          url: import.meta.env.VITE_NOTE_CRDT_WEBSOCKET,
          name: socketId,
          document: ydoc,
          token: websocketToken,
        });
  providerRegistry.set(socketId, { ydoc, providerWebsocket, references: 1 });
  return { ydoc, providerWebsocket };
};

const getInputElements = () =>
  Array.from(document.querySelectorAll(".js-meeting-topic-input"));

const getCurrentIndex = (elements: Element[]) => {
  const active = document.activeElement;
  if (!active) return -1;
  return elements.findIndex((el) => el === active || el.contains(active));
};

const focusInput = (el: Element) => {
  if (el instanceof HTMLTextAreaElement) {
    el.focus();
  } else {
    const tiptap = el as Element & { editor?: Editor };
    tiptap.editor?.chain().focus().run();
  }
};

export const focusNextEditor = (): boolean => {
  const elements = getInputElements();
  const idx = getCurrentIndex(elements);
  if (idx !== -1 && idx < elements.length - 1) {
    focusInput(elements[idx + 1]);
    return true;
  }
  return false;
};

export const focusPrevEditor = (): boolean => {
  const elements = getInputElements();
  const idx = getCurrentIndex(elements);
  if (idx > 0) {
    focusInput(elements[idx - 1]);
    return true;
  }
  return false;
};

export const MeetingKeyboardShortcuts = Extension.create({
  name: "keyboardShortcuts",

  addKeyboardShortcuts() {
    return {
      ArrowDown: () => {
        const { view, state } = this.editor;
        // Let the slash-command suggestion own arrow keys while open.
        if (SuggestionPluginKey.getState(state)?.active) return false;
        const isOnLastVisualLine = view.endOfTextblock("down");
        if (!isOnLastVisualLine) return false;
        const { $head } = state.selection;
        const $end = TextSelection.atEnd(state.doc).$head;
        const isInLastTextblock =
          $head.start($head.depth) === $end.start($end.depth);
        if (isInLastTextblock) {
          return focusNextEditor();
        }
        return false;
      },
      ArrowUp: () => {
        const { view, state } = this.editor;
        if (SuggestionPluginKey.getState(state)?.active) return false;
        const isOnFirstVisualLine = view.endOfTextblock("up");
        if (!isOnFirstVisualLine) return false;
        const { $head } = state.selection;
        const $start = TextSelection.atStart(state.doc).$head;
        const isInFirstTextblock =
          $head.start($head.depth) === $start.start($start.depth);
        if (isInFirstTextblock) {
          return focusPrevEditor();
        }
        return false;
      },
    };
  },
});

export const getExtensions = ({
  context,
  paidFeatures,
  placeholder,
  emptyPlaceholder,
  uploadVariable,
  atMentionSuggestions,
  onClickCommentUuid,
  showPlaceholderOnlyWhenEditable = true,
  history,
  disableSlashCommands = false,
  onModEnter,
  onEnter,
}: {
  context: any;
  paidFeatures: PaidFeaturesNode;
  placeholder: string;
  emptyPlaceholder: string | any;
  uploadVariable: {
    artifactId?: number;
    topicId?: number;
  };
  disableSlashCommands?: boolean;
  atMentionSuggestions: any;
  onClickCommentUuid?: (uuid: string) => void;
  onModEnter?: () => void;
  onEnter?: () => void;
  showPlaceholderOnlyWhenEditable?: boolean;
  history?: false;
  tiptapAiJwt?: string;
}) => {
  const extensions = [
    Decision.configure({
      ...context,
      permission: false,
    }),
    Feedback.configure({
      ...context,
      permission: true,
    }),
    Recognition.configure({
      ...context,
      permission: true,
    }),
    ActionItem.configure({
      ...context,
      permission: true,
    }),
    Document.configure({
      ...context,
      permission: false,
    }),
    Goal.configure({
      ...context,
      permission: paidFeatures.canCreateGoals,
    }),
    KpiEmbedExtension.configure(context),
    KpiGroupEmbedExtension.configure(context),
    MeetingEmbedExtension.configure(context),
    AsanaExtension.configure(context),
    GapCursor,
    UrlPreview,
    Iframe,
    ExplorerExtension,
    CustomLink.configure({
      openOnClick: true,
      autolink: true,
      HTMLAttributes: {
        class: "js-wysiwyg-link",
      },
    }),
    StarterKit.configure({
      codeBlock: false,
      bulletList: false,
      orderedList: false,
      listItem: false,
      undoRedo: history,
      // Tiptap 3 added these to StarterKit. We add our own link,
      // underline and list keymap below, and never had a trailing node.
      link: false,
      underline: false,
      listKeymap: false,
      trailingNode: false,
    }),
    Timer,
    ButtonExtension,
    Underline,
    CustomListItem,
    BulletList.configure({ itemTypeName: "listItem" }),
    OrderedList.configure({ itemTypeName: "listItem" }),
    ListKeymap,
    Placeholder.configure({
      placeholder: ({ editor: editorPlaceholder }) => {
        if (editorPlaceholder.isEmpty && emptyPlaceholder) {
          return emptyPlaceholder;
        }
        return placeholder;
      },
      showOnlyWhenEditable: showPlaceholderOnlyWhenEditable,
      showOnlyCurrent: true,
      includeChildren: false,
    }),
    Table.configure({ resizable: true }),
    TableRow,
    TableHeader,
    TableCell,
    !disableSlashCommands &&
      Commands.configure({
        suggestion: {
          items: getSuggestionItems({
            permissionGoal: !!paidFeatures.canCreateGoals,
          }),
          render: renderItems,
        },
      }),
    !!onClickCommentUuid && Comment.configure({ onClickCommentUuid }),
    Typography,
    CustomImage(asyncUploadImage(uploadVariable)),
    CodeBlockLowlight.extend({
      addNodeView() {
        return ReactNodeViewRenderer(CodeBlockComponent);
      },
    }).configure({ lowlight }),
    Extension.create({
      addKeyboardShortcuts() {
        return {
          // Inside topic notes (`onEnter` provided):
          //   Enter / Shift-Enter -> split into a new block (default).
          //   Cmd/Ctrl-Enter      -> create a new topic.
          // Outside topic notes: defaults apply for Enter and Shift-Enter,
          // and Mod-Enter falls back to the legacy `onModEnter` callback.
          "Shift-Enter": ({ editor }) => {
            if (!onEnter) return false;
            return editor.chain().focus().splitBlock().run();
          },
          "Mod-Enter": () => {
            if (onEnter) {
              onEnter();
              return true;
            }
            onModEnter?.();
            return true;
          },
        };
      },
    }),
    MeetingKeyboardShortcuts,
    atMentionSuggestions &&
      CustomMention.configure({
        HTMLAttributes: { class: "mention" },
        suggestion: atMentionSuggestions,
      }),
    Highlight.configure({ multicolor: true }),

    // DEPRECATED: do not remove otherwise it'll break the schema and will empty notes
    DeprecatedRatingExtension.configure(context),
    DeprecatedGithubExtension,
    DeprecatedKpiExtension.configure(context),
  ];
  return extensions;
};

export const getPluginClickOn = (name: string) =>
  new Plugin({
    key: new PluginKey(`${name}-click-on`),
    props: {
      // https://github.com/ueberdosis/tiptap/issues/1053
      // https://prosemirror.net/docs/ref/#view.EditorProps.handleClickOn
      // Helps control click behaviour
      handleClickOn: (_view, _pos, node) => {
        if (node.type.name === name) {
          return true;
        }
        return false;
      },
    },
  });

export const getTiptapJsonToText = (node: JSONContent): string => {
  if (node.type === "text") return node.text || "";
  if (node.type === "hardBreak") return "\n";
  const children = Array.isArray(node.content) ? node.content : [];
  if (node.type === "bulletList" || node.type === "orderedList") {
    return children
      .map((item, index) => {
        const marker =
          node.type === "bulletList"
            ? "- "
            : `${(node.attrs?.start ?? 1) + index}. `;
        return (
          marker +
          getTiptapJsonToText(item).replace(
            /\n/g,
            "\n" + " ".repeat(marker.length),
          )
        );
      })
      .join("\n");
  }
  const separator = ["paragraph", "heading", "codeBlock"].includes(
    node.type || "",
  )
    ? ""
    : node.type === "tableRow"
      ? "\t"
      : "\n";
  return children.map(getTiptapJsonToText).join(separator);
};

export const getPluginHandlePaste = (
  self: any,
  cb: (text: string, view: any) => boolean,
) =>
  new Plugin({
    key: new PluginKey(`${self.name}-paste`),
    props: {
      handlePaste: (view, event) => {
        if (!event.clipboardData) {
          return false;
        }
        // don’t create a new code block within code blocks
        if (self.editor.isActive(self.type.name)) {
          return false;
        }
        const text = event.clipboardData.getData("text/plain");
        return cb(text, view);
      },
    },
  });
